import { X, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useNavigate } from 'react-router-dom';

interface CartPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartPanel({ isOpen, onClose }: CartPanelProps) {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem } = useCartStore();
  const itemCount = items.reduce((sum, item) => sum + item.cantidad, 0);

  if (!isOpen) return null;

  const handleCheckout = () => {
    onClose();
    navigate('/carrito');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 animate-fadeIn bg-black/50" onClick={onClose} />

      <div className="fixed right-0 top-0 z-50 flex h-full w-full animate-slideInRight flex-col bg-[#f5f5f5] shadow-2xl sm:max-w-xl">
        <div className="border-b border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[#0d141b]">Carrito de Compra</h2>
            <button
              onClick={onClose}
              className="rounded-lg p-2 transition-colors hover:bg-slate-100"
              type="button"
              aria-label="Cerrar carrito"
            >
              <X className="h-6 w-6 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-3xl bg-white text-slate-300 shadow-sm">
                <ShoppingCart className="h-12 w-12" />
              </div>
              <p className="text-lg font-semibold text-[#0d141b]">Tu carrito está vacío</p>
              <p className="mt-2 text-sm text-[#4c739a]">Añade artículos para preparar el pedido.</p>
            </div>
          ) : (
            <div className="p-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.articulo.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                        <p className="mb-1 text-base font-semibold leading-tight text-[#0d141b]">
                          {item.articulo.nombre}
                        </p>
                        </div>
                        <button
                          onClick={() => removeItem(item.articulo.id)}
                          className="rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-600"
                          type="button"
                        >
                          Eliminar
                        </button>
                      </div>

                      <div className="mt-3 flex items-center gap-3">
                        <button
                          onClick={() => {
                            if (item.cantidad === 1) {
                              removeItem(item.articulo.id);
                            } else {
                              updateQuantity(item.articulo.id, item.cantidad - 1);
                            }
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:bg-slate-50"
                          type="button"
                          aria-label={`Reducir cantidad de ${item.articulo.nombre}`}
                        >
                          <Minus className="h-4 w-4 text-slate-600" />
                        </button>

                        <span className="w-6 text-center text-sm font-bold text-[#0d141b]">{item.cantidad}</span>

                        <button
                          onClick={() => updateQuantity(item.articulo.id, item.cantidad + 1)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:bg-slate-50"
                          type="button"
                          aria-label={`Aumentar cantidad de ${item.articulo.nombre}`}
                        >
                          <Plus className="h-4 w-4 text-slate-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 bg-white p-4">
            <div className="mb-4">
              <span className="text-lg font-semibold text-[#0d141b]">
                {itemCount} {itemCount === 1 ? 'artículo' : 'artículos'} en el carrito
              </span>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full rounded-xl bg-[#27648d] py-4 text-lg font-bold text-white shadow-md transition-colors hover:bg-[#1f5479]"
              type="button"
            >
              Finalizar Pedido
            </button>
          </div>
        )}
      </div>
    </>
  );
}
